import * as AWS from "aws-sdk";
import retryFetch from "../../../utils/retryFetch";

const SAVED_BUFFER_SIZE = 1000; // 5k * 4 bytes = 20k per track
const LOCAL_STORAGE_PREFIX = "waveform_summary.";

class WaveformDisplayCache {
  /** @type {Array<number>} */
  savedBuffer = [];

  /** @type {boolean} */
  sentToServer = false;

  /** @type {boolean} */
  requestedFromServer = false;

  /**
   * @param {AudioBuffer} audioBuffer
   * @param {number} resolution
   * @returns {Array<number>}
   */
  computeBuffer = (audioBuffer, resolution) => {
    const leftChannel = audioBuffer.getChannelData(0);
    const bufferPerPixel = leftChannel.length / resolution;
    const wave = [];
    for (let x = 0; x < resolution; x += 1) {
      // on which line do we get ?
      const i = Math.floor(x * bufferPerPixel);

      const AVG_OVER_SAMPLES = 100;

      const avgFrom = Math.max(0, i - AVG_OVER_SAMPLES);
      let sum = 0;
      for (let j = avgFrom; j < i; j += 1) {
        sum += Math.abs(leftChannel[j]);
      }
      sum /= AVG_OVER_SAMPLES;
      wave.push(sum);
    }
    return wave;
  };

  /**
   * This releases the buffer, and clears our flags saying that we already requested it from the server.
   */
  unload = () => {
    this.savedBuffer = [];
    this.requestedFromServer = false;
  };

  /**
   * This allows us to increase the resolution geometrically if needed.
   *
   * @param resolution
   * @param audioBuffer
   */
  /**
   * @param {number} resolution
   * @param {AudioBuffer} audioBuffer
   */
  requestResolution = (resolution, audioBuffer) => {
    if (resolution > this.savedBuffer.length) {
      this.savedBuffer = this.computeBuffer(
        audioBuffer,
        this.savedBuffer.length * 2
      );
    }
  };

  /**
   * This produces a summary of the track for rendering waveforms. We save this summary to
   * localStorage so we can display waveforms without downloading the track.
   */
  /**
   * @param {AudioBuffer | null} audioBuffer
   * @param {string} trackId
   * @param {() => void} rerenderCallback
   */
  recomputeSavedBuffer = (audioBuffer, trackId, rerenderCallback) => {
    if (audioBuffer != null) {
      const wave = this.computeBuffer(audioBuffer, SAVED_BUFFER_SIZE);

      // 1. Try to save to localStorage

      window.localStorage.removeItem(LOCAL_STORAGE_PREFIX + trackId);
      /*
      try {
        window.localStorage.setItem(
          LOCAL_STORAGE_PREFIX + trackId,
          JSON.stringify(wave)
        );
      } catch (e) {
        // out of storage space
        console.error('we ran out of storage space in localStorage');
      }
       */

      // 2. Always save to the server

      if (!this.sentToServer) {
        this.sentToServer = true;

        // Set up S3

        AWS.config.update({
          region: "us-west-2",
          credentials: new AWS.CognitoIdentityCredentials({
            IdentityPoolId: "us-west-2:cd3c7822-1c50-4010-bb08-8a5548378ef5"
          })
        });

        const s3 = new AWS.S3({
          apiVersion: "2006-03-01",
          params: { Bucket: "ascheandspencer-waveforms" }
        });

        // Upload the mp3

        s3.upload(
          {
            Key: `waveform/${trackId.toString()}`,
            Body: JSON.stringify(wave),
            ACL: "public-read"
          },
          err => {
            if (err) {
              console.error("There was an error cacheing your waveform");
              console.error(err);
            } else {
              // console.log("uploaded to S3");
            }
          }
        );
      }

      // Re-render

      this.savedBuffer = wave;
      rerenderCallback();
    } else {
      // `https://ascheandspencer-waveforms.s3-us-west-2.amazonaws.com/waveform/${trackId}`
      if (!this.requestedFromServer) {
        this.requestedFromServer = true;
        retryFetch(
          `https://ascheandspencer-waveforms.s3-us-west-2.amazonaws.com/waveform/${trackId}`,
          {
            method: "GET"
          }
        )
          .then(r => r.json())
          .then((response) => {
            this.savedBuffer = response;
            this.sentToServer = true;
            rerenderCallback();
          })
          .catch(() => {
            // Ignored. This just means that we've previously played a track, but haven't recomputed
            // the waveform yet.
          });
      }
    }
  };
}

export default WaveformDisplayCache;
